// รายชื่อผู้เข้าถึงแบรนด์หนึ่ง พร้อมสิทธิ์ของแต่ละคน
//
// ใช้สองที่: แผง "ผู้เข้าถึง" ในหน้า config ของแบรนด์ (Admin) และหน้า Team ของแบรนด์นั้น
// (คนในทีมเอง) — เป็น component เดียวกันเพื่อให้สองที่เห็นและแก้ของชุดเดียวกันจริง
// ไม่ใช่ markup คนละชุดที่ค่อย ๆ เพี้ยนออกจากกัน
//
// canManage เป็นแค่การจัดหน้าจอ — ตัวกันสิทธิ์จริงคือ middleware ฝั่ง server
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authKeys, fetchEmployees, type Employee } from "@features/auth/api/auth-api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { apiErrorMessage } from "@shared/api/client";
import { IconSearch } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import {
  addBrandMember,
  fetchBrandMembers,
  removeBrandMember,
  setBrandMemberRole,
  settingsKeys,
  type MemberRole,
} from "../api/settings-api";
import { PersonAvatar } from "@shared/ui/person-avatar";
import { ROLE_LABEL, ROLE_SHORT } from "../lib/member-roles";
import {
  byDepartmentThenName,
  fromEmployee,
  fromMember,
  initialOf,
  primaryName,
  type PersonView,
} from "../lib/person-view";
import { PersonCell } from "./person-cell";

export function MemberRoster({
  brandId,
  canManage,
  meCode,
  allowSelfEdit = false,
  layout = "rows",
  enabled = true,
}: {
  brandId: string;
  /** เปิดให้เพิ่ม/ถอด/เปลี่ยนสิทธิ์ได้ไหม — Admin หรือคนที่ถือสิทธิ์ manage ของแบรนด์นี้ */
  canManage: boolean;
  /** รหัสของคนที่กำลังดูอยู่ — ใช้ทำเครื่องหมาย "คุณ" และกันแก้สิทธิ์ตัวเอง */
  meCode?: string;
  /**
   * แก้แถวของตัวเองได้ไหม — จริงเฉพาะ Admin (ตรงกับที่ server ยอม)
   * ผู้ดูแลธรรมดาแก้ไม่ได้ เพื่อกันแบรนด์ที่มีผู้ดูแลคนเดียวเผลอถอดตัวเองออกจนไม่เหลือใครแก้ต่อ
   */
  allowSelfEdit?: boolean;
  /**
   * rows = แถวยาวเต็มความกว้าง (แผงในหน้า config ซึ่งเป็นคอลัมน์แคบ)
   * cards = การ์ดเรียงสามใบต่อแถว (หน้า Team ซึ่งกว้างเต็มหน้า) — อ่านว่าใครอยู่ทีมนี้
   * ได้เร็วกว่าเพราะเห็นรูปกับชื่อเป็นก้อน ไม่ต้องกวาดสายตาตามบรรทัด
   */
  layout?: "rows" | "cards";
  enabled?: boolean;
}) {
  const qc = useQueryClient();
  const token = wazzupToken();
  const [q, setQ] = useState("");
  const [addAs, setAddAs] = useState<MemberRole>("viewer");
  const [msg, setMsg] = useState("");

  const members = useQuery({
    queryKey: settingsKeys.brandMembers(brandId),
    queryFn: () => fetchBrandMembers(brandId),
    enabled,
    retry: false,
  });

  /**
   * รายชื่อพนักงานทั้งบริษัท — ชุดเดียวกับหน้า Setting → Staff จึงใช้ cache ร่วมกันได้
   *
   * โหลดแม้ตอนแก้อะไรไม่ได้ เพราะแผนก ตำแหน่ง และรูป ไม่ได้อยู่ในฐานข้อมูลเรา
   * ถ้าไม่ดึงมา คนที่เป็นผู้ดูจะเห็นแค่ชื่อกับรหัส ซึ่งอ่านไม่ออกว่าใครอยู่ทีมไหน
   */
  const staff = useQuery({
    queryKey: authKeys.employees,
    queryFn: () => fetchEmployees(token!),
    enabled: enabled && !!token,
    retry: false,
    staleTime: 10 * 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: settingsKeys.brandMembers(brandId) });

  const add = useMutation({
    mutationFn: (p: Employee) =>
      addBrandMember(brandId, {
        empCode: p.empCode,
        empName: p.empThaiName || p.empEngName || p.nickName || p.empCode,
        role: addAs,
      }),
    onSuccess: () => {
      setMsg("");
      setQ("");
      void invalidate();
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
  });

  const changeRole = useMutation({
    mutationFn: (v: { empCode: string; role: MemberRole }) =>
      setBrandMemberRole(brandId, v.empCode, v.role),
    onSuccess: () => {
      setMsg("");
      void invalidate();
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (empCode: string) => removeBrandMember(brandId, empCode),
    onSuccess: () => {
      setMsg("");
      void invalidate();
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
  });

  const current = useMemo(() => members.data ?? [], [members.data]);
  const currentCodes = useMemo(() => new Set(current.map((m) => m.empCode)), [current]);
  const staffByCode = useMemo(
    () => new Map((staff.data ?? []).map((p) => [p.empCode, p])),
    [staff.data],
  );

  // เรียงฝั่ง client เพราะแผนกไม่ได้อยู่ในฐานข้อมูลเรา — server เรียงให้ไม่ได้
  const rows = useMemo(
    () =>
      current
        .map((m) => ({ ...fromMember(m, staffByCode.get(m.empCode)), role: m.role }))
        .sort(byDepartmentThenName),
    [current, staffByCode],
  );

  // ค้นเฉพาะตอนพิมพ์ — รายชื่อพนักงานมีเป็นพันคน โชว์ทั้งหมดไม่มีประโยชน์
  // ค้นได้ทั้งชื่อไทยและอังกฤษ รวมถึงตำแหน่งกับแผนกที่แสดงอยู่ในแถว
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return (staff.data ?? [])
      .filter((p) => !currentCodes.has(p.empCode))
      .filter((p) =>
        [p.empCode, p.empThaiName, p.empEngName, p.nickName, p.positionName, p.departmentName].some(
          (v) => (v || "").toLowerCase().includes(needle),
        ),
      )
      .slice(0, 8);
  }, [staff.data, q, currentCodes]);

  const managers = current.filter((m) => m.role === "manage").length;

  return (
    <>
      {canManage && (
        <div className="mem-pick">
          <div className="mem-pick-head">
            <div className="search-box grow-box">
              <IconSearch />
              <input
                type="text"
                placeholder="พิมพ์ชื่อไทยหรืออังกฤษ ชื่อเล่น ตำแหน่ง แผนก หรือรหัสพนักงาน เพื่อเพิ่มคน…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                disabled={!token || staff.isError}
              />
            </div>
            {/* เลือกสิทธิ์ก่อนกดเพิ่ม — เปลี่ยนทีหลังที่แถวของคนนั้นก็ได้ */}
            <label className="camp-select-wrap">
              <span className="muted camp-select-lbl">เพิ่มเป็น</span>
              <select
                className="camp-select"
                value={addAs}
                onChange={(e) => setAddAs(e.target.value as MemberRole)}
              >
                <option value="viewer">{ROLE_LABEL.viewer}</option>
                <option value="manage">{ROLE_LABEL.manage}</option>
              </select>
            </label>
          </div>
          {!token ? (
            <p className="muted small-note">
              ต้องเข้าสู่ระบบก่อนจึงจะดึงรายชื่อพนักงานมาให้เลือกได้
            </p>
          ) : staff.isError ? (
            <p className="muted small-note">
              ดึงรายชื่อพนักงานไม่สำเร็จ: {apiErrorMessage(staff.error)}
            </p>
          ) : q.trim() && !matches.length ? (
            <p className="muted small-note">ไม่พบคนที่ตรงกับ &quot;{q.trim()}&quot;</p>
          ) : null}

          {!!matches.length && (
            <div className="mem-results">
              {matches.map((p) => (
                <button
                  key={p.empCode}
                  type="button"
                  className="mem-result"
                  disabled={add.isPending}
                  onClick={() => add.mutate(p)}
                >
                  <PersonCell v={fromEmployee(p)} />
                  <span className="code-chip">{p.empCode}</span>
                  <span className="mem-add">+ เพิ่ม</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {msg && <p className="muted small-note">{msg}</p>}

      <div className="cfg-sub">
        <h4>
          ผู้เข้าถึงตอนนี้ ({current.length})
          {!!current.length && <span className="muted small-note"> · ผู้ดูแล {managers} คน</span>}
        </h4>
        {members.isPending ? (
          <p className="muted">กำลังโหลด…</p>
        ) : members.isError ? (
          <p className="muted">โหลดรายชื่อไม่สำเร็จ: {apiErrorMessage(members.error)}</p>
        ) : !current.length ? (
          <p className="muted small-note">
            ยังไม่ได้กำหนดใคร — ตอนนี้ผู้ใช้ที่ถูกจำกัดสิทธิ์จะยังไม่เห็นแบรนด์นี้เลย
          </p>
        ) : (
          <div className={layout === "cards" ? "team-grid" : "mem-list"}>
            {rows.map((v) => {
              // แก้สิทธิ์ตัวเองไม่ได้ (ฝั่ง server ก็ปฏิเสธ) — กันผู้ดูแลคนเดียวถอดตัวเอง
              // ออกจนไม่เหลือใครแก้รายชื่อต่อได้ · Admin ไม่ติดข้อนี้
              const self = !!meCode && v.empCode === meCode;
              const editable = canManage && !(self && !allowSelfEdit);
              const role = (
                <RoleControl
                  value={v.role}
                  editable={editable}
                  short={layout === "cards"}
                  busy={changeRole.isPending}
                  onChange={(next) => changeRole.mutate({ empCode: v.empCode, role: next })}
                />
              );
              const drop = editable ? (
                <button
                  type="button"
                  className="icon-btn"
                  title="เอาออกจากทีม"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(v.empCode)}
                >
                  ✕
                </button>
              ) : null;

              return layout === "cards" ? (
                <MemberCard key={v.empCode} v={v} self={self} role={role} drop={drop} />
              ) : (
                <div className="mem-row" key={v.empCode}>
                  <PersonCell v={v} />
                  <span className="code-chip">{v.empCode}</span>
                  {role}
                  {drop ?? (
                    <span className="mem-role-lock muted" title={self ? SELF_NOTE : ""}>
                      {self ? "คุณ" : ""}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

const SELF_NOTE = "เปลี่ยนสิทธิ์ของตัวเองไม่ได้ — ให้ผู้ดูแลคนอื่นหรือ Admin เป็นคนแก้";

/**
 * สิทธิ์ของคนหนึ่งคน — เป็น capsule เมื่อแก้ไม่ได้ และเป็น dropdown ทรงเดียวกันเมื่อแก้ได้
 *
 * ใช้ทรงเดียวกันทั้งสองสถานะ เพื่อให้ตำแหน่งและขนาดของการ์ดไม่ขยับตามสิทธิ์ของคนดู
 */
function RoleControl({
  value,
  editable,
  short,
  busy,
  onChange,
}: {
  value: MemberRole;
  editable: boolean;
  /** true = แสดงคำเดียว (การ์ด) · false = คำเต็มพร้อมคำแปล (แถว) */
  short: boolean;
  busy: boolean;
  onChange: (next: MemberRole) => void;
}) {
  const label = short ? ROLE_SHORT : ROLE_LABEL;
  const cls = cn(
    short ? "role-pill" : "mem-role",
    value === "manage" && (short ? "role-pill-manage" : "mem-role-manage"),
  );
  if (!editable) return <span className={cls}>{label[value]}</span>;
  return (
    <select
      className={cn(cls, short ? "role-pill-edit" : "camp-select mem-role-select")}
      value={value}
      disabled={busy}
      title="เปลี่ยนสิทธิ์ของคนนี้"
      onChange={(e) => onChange(e.target.value as MemberRole)}
    >
      <option value="viewer">{label.viewer}</option>
      <option value="manage">{label.manage}</option>
    </select>
  );
}

/** คนหนึ่งคนในหน้า Team — รูป ชื่อ ตำแหน่ง แผนก แล้วค่อยเป็นสิทธิ์กับปุ่มเอาออก */
function MemberCard({
  v,
  self,
  role,
  drop,
}: {
  v: PersonView & { role: MemberRole };
  self: boolean;
  role: React.ReactNode;
  drop: React.ReactNode;
}) {
  return (
    <div className={cn("team-card", self && "team-card-self")}>
      <PersonAvatar url={v.photo} initial={initialOf(v)} />
      <div className="team-card-body">
        <span className="team-card-name" title={primaryName(v)}>
          {primaryName(v)}
          {v.nick && <span className="mem-nick"> ({v.nick})</span>}
        </span>
        <span className="team-card-pos">{v.position || v.eng || "—"}</span>
        <span className="team-card-dept">{v.department || "ไม่ระบุแผนก"}</span>
        <div className="team-card-foot">
          {role}
          <span className="code-chip">{v.empCode}</span>
          {/* "คุณ" แทนคำอธิบายว่าทำไมการ์ดใบนี้ไม่มีปุ่มเอาออก */}
          {self && (
            <span className="muted team-card-you" title={SELF_NOTE}>
              คุณ
            </span>
          )}
        </div>
      </div>
      {drop}
    </div>
  );
}
